import type { RoutePlan } from '@gpx-spa/geo-domain';
import type { ShipmentFormStopRow } from '@/stores/useShipmentFormStore';
import { haveFormRoutesChanged } from '@/components/shipment/form/utils/persist/persistFormRoutes';
import {
  buildShipmentSavePlan,
  savePlanHasWork,
  type ShipmentSavePlan,
} from '@/components/shipment/form/save/shipment-save-plan';
import type { ShipmentFormSavePolicy } from '@/composables/shipment-form-save-policy';

export type ShipmentFormChangeSet = {
  metadata: 'unchanged' | 'changed';
  stops: 'unchanged' | 'changed';
  stopsInvalidatesRoutes: boolean;
  routes: 'unchanged' | 'upsert' | 'recreate';
  devices: 'unchanged' | 'changed';
};

export type ShipmentFormChangeContext = {
  metadataChanged: boolean;
  /** @deprecated Ignored; stop write uses persisted ids + snapshot. Kept for call-site compat. */
  stopsSyncedOnDeviceApi?: boolean;
  stops: ShipmentFormStopRow[];
  stopsChangedSincePersisted: boolean;
  persistedStopsPayloadSnapshot: string | null;
  deviceIdsChanged: boolean;
  selectedByPathId: Record<string, boolean>;
  routePlan: RoutePlan | null;
};

export function changeSetFromSavePlan(
  plan: ShipmentSavePlan,
): ShipmentFormChangeSet {
  return {
    metadata: plan.metadata === 'patch' ? 'changed' : 'unchanged',
    stops: plan.stops.method === 'skip' ? 'unchanged' : 'changed',
    stopsInvalidatesRoutes: plan.stopsInvalidatesRoutes,
    routes:
      plan.routes.mode === 'skip'
        ? 'unchanged'
        : plan.routes.mode === 'recreate'
          ? 'recreate'
          : 'upsert',
    devices: plan.devices === 'post' ? 'changed' : 'unchanged',
  };
}

export function detectShipmentFormChanges(
  input: ShipmentFormChangeContext,
  policy: ShipmentFormSavePolicy,
  options?: { includePublish?: boolean; createPublishRouteUpsert?: boolean },
): ShipmentFormChangeSet {
  return changeSetFromSavePlan(
    buildShipmentSavePlanFromChangeContext(input, policy, options),
  );
}

export function buildShipmentSavePlanFromChangeContext(
  input: ShipmentFormChangeContext,
  policy: ShipmentFormSavePolicy,
  options?: { includePublish?: boolean; createPublishRouteUpsert?: boolean },
): ShipmentSavePlan {
  return buildShipmentSavePlan({
    policy,
    metadataChanged: input.metadataChanged,
    stopsChangedSincePersisted: input.stopsChangedSincePersisted,
    persistedStopsPayloadSnapshot: input.persistedStopsPayloadSnapshot,
    stops: input.stops,
    deviceIdsChanged: input.deviceIdsChanged,
    routeComposerChanged: haveFormRoutesChanged(input.selectedByPathId),
    routePlan: input.routePlan,
    includePublish: options?.includePublish === true,
    createPublishRouteUpsert: options?.createPublishRouteUpsert === true,
  });
}

export function shipmentFormChangeSetHasWork(
  changeSet: ShipmentFormChangeSet,
): boolean {
  return (
    changeSet.metadata !== 'unchanged' ||
    changeSet.stops !== 'unchanged' ||
    changeSet.routes !== 'unchanged' ||
    changeSet.devices !== 'unchanged'
  );
}

export { savePlanHasWork };
